import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";

const IMAGE_TYPES = ["hero", "artist_photo", "artwork", "label", "general"];

export default function ImageManager() {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState("general");
  const inputRef = useRef(null);

  const fetch_ = () => {
    const params = filter ? { image_type: filter } : {};
    api.get("/images", { params }).then(r => setImages(r.data)).catch(() => {});
  };
  useEffect(() => { fetch_(); }, [filter]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/images?title=${encodeURIComponent(file.name)}&image_type=${uploadType}`, formData);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    toast.success(`${files.length} image(s) uploaded`);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    fetch_();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try { await api.delete(`/images/${id}`); toast.success("Deleted"); fetch_(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-image-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl">IMAGES</h1>
          <p className="text-white/40 text-sm">{images.length} images</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={uploadType} onChange={e => setUploadType(e.target.value)} className="bg-[#111111] border border-white/10 px-3 py-2 rounded text-sm text-white outline-none">
            {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
          <Button data-testid="upload-image-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Upload
          </Button>
          <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition-colors ${!filter ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-white/40 hover:text-white"}`}>All</button>
        {IMAGE_TYPES.map(t => (
          <button key={t} onClick={() => setFilter(t)} className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition-colors ${filter === t ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-white/40 hover:text-white"}`}>
            {t.replace("_", " ")}
          </button>
        ))}
      </div>

      {images.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No images yet. Upload your first image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {images.map((img) => (
            <div key={img.id} className="group relative bg-[#111111] border border-white/5 rounded overflow-hidden hover:border-white/10 transition-colors">
              <div className="aspect-square bg-[#0A0A0A] flex items-center justify-center">
                <img src={getFileUrl(img.storage_path)} alt={img.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <p className="text-xs truncate text-white/60">{img.title}</p>
                <p className="text-[10px] text-[#D4AF37] uppercase">{img.image_type}</p>
              </div>
              <button onClick={() => handleDelete(img.id)} className="absolute top-2 right-2 w-7 h-7 bg-black/60 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white/60 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
